import { createReduxEnhancer } from '@sentry/react'
import { applyMiddleware, compose, createStore } from 'redux'
import thunk from 'redux-thunk'
import sha1 from 'sha1'
import uuid from 'uuid'

import * as types from '../constants/ActionTypes'
import {
  dumpStoreMiddleware,
  fetchMiddleware,
  loadStoreMiddleware,
  rerouteMiddleware,
} from '../middlewares/index'
import rootReducer from '../reducers/Buy'

const initialState = {
  customer: {
    access_time_session: new Date(),
    id_cliente: null,
    name: null,
    surname: null,
    registration_lead: false,
    newsletter_lead: false,
    tracciato_sent: false,
    email: null,
    phone: null,
    token: null,
    data_di_nascita: { raw: null },
    data_di_nascita_nlt: { raw: null }, // 18-75
    data_di_nascita_findomestic: { raw: null }, // 18-75
    data_rilascio_permesso_soggiorno: { raw: null },
    data_scadenza_permesso_soggiorno: { raw: null },
    data_rilascio_documento: { raw: null },
    tipo_permesso_soggiorno: null,
    comune_nascita: null,
    sesso: null,
    codice_fiscale: null,
    altre_carte: [],
    politicamente_esposto: 0,
    domicilio_diverso: 0,
    condizioni_brumbrum_obbligatorie: false,
    cittadinanza: null,
    nome: null,
    cognome: null,
    registered_email: null,
    agevolazione_iva: null,
    privacy_agevolazioni_fiscali: null,
  },
  documentation: {
    cid: null,
    cf: null,
    permit: null,
    residence: null,
  },
  privacy: {
    consenso_obbligatorio_privacy_brumbrum: false,
    newsletter: false,
  },
  acceptance: {
    sacci: false,
    privacy: false,
    extended: false,
    associated: false,
    acceptance: false,
  },
  product: {
    id_auto: null,
    marca: 'nuova',
    modello: 'auto',
  },
  pricing: {
    product: null,
    data: {
      result: {},
    },
  },
  findomestic: {
    altre_carte: [],
    firmaAssicurazioni: [],
    firmaAssicurazioniCheckBoxes: {},
    certificatoLinks: [],
    certificatoCheckBoxes: {},
    contrattoInfocertLinks: [],
    contrattoInfocertCheckBoxes: {},
    contrattoFindomesticLinks: [],
    contrattoFindomesticCheckBoxes: {},
    settoreBySituazione: {},
    occupazioneBySituazioneAndSettore: {},
    pin: '',
    can_sign: false,
    credentials: {},
    conferma_pin: '',
    otp: '',
    meta: {},
    CodiciAbitazione: [],
    CodiciCarta: [],
    CodiciDestinatario: [],
    CodiciDocumento: [],
    CodiciNazionalita: [],
    CodiciPagamento: [],
    CodiciReperibilita: [],
    CodiciScadenza: [],
    CodiciStatoCivile: [],
    CodiciTipoAssicurazione: [],
    CodiciTipoDocumentoSoggiorno: [],
    CodiciTipoLavoro: [],
    CodiciTipoVeicolo: [],
    CodiciTitoloStudio: [],
    MappaCodiciLavoroSettoriAttivita: [],
    CodiciSettoreAttivita: [],
    CodiciSettoreAttivitaById: [],
    dati: {
      altre_fonti_reddito: '',
      altre_fonti_switch: '0',
      anzianita: '',
      anni_apertura_conto: '',
      indirizzo_lavoro: '',
      civico_lavoro: '',
      comune_lavoro: '',
      datore_lavoro: '',
      telefono_fisso_lavoro: '',
      autocertificazione_2: '',
      carica_privata: '',
      carica_pubblica: '',
      carica_pubblica_switch: '0',
      consenso_base_privacy_arval: '',
      iban: '',
      nome_societa: '',
      costo_abitazione: '',
      occupazione: '',
      professione: '',
      reddito_coniuge: '',
      seconda_cittadinanza: '',
      settore: '',
      situazione_lavorativa: '',
      situazione_abitativa: '',
      stato_civile: '',
      tipo_contratto: '',
      residente_dal: { raw: null },
      data_apertura_conto: { raw: null },
      comune_precedente: '',
      cap_precedente: '',
      indirizzo_precedente: '',
      civico_precedente: '',
      flagQualita: '0',
      flagSocietaTerze: '0',
      flagPromozioni: '0',
      flag675: '1',
      condizioni_brumbrum_obbligatorie: false,
      documenti_necessari: [],
      data_inizio_attivita: { raw: null },
      data_scadenza_contratto: { raw: null },
      coniuge_data_scadenza_contratto: { raw: null },
      coniuge_data_inizio_attivita: { raw: null },
      coniuge_occupazione: '',
      coniuge_settore: '',
      coniuge_situazione_lavorativa: '',
      coniuge_reddito_mensile_lavoro: '',
      paese_di_attivita_principale: '',
      reddito_mensile_lavoro: '',
      numero_documento_retro_patente: '',
      settore_rischio_aml: '',
      coniuge_settore_rischio_aml: '',
    },
    servizi_assicurativi: [],
    servizi_assicurativi_selezionati: [],
    finanziamentoAssicurato: {},
  },
  financements: {
    default: [],
    custom: null,
    quota_da_finanziare: null,
    rata_mensile_desiderata: null,
  },
  ordine: {
    acconti: 0,
    id_ordine: null,
    id_finanziamento: null,
    costi_extra: 0,
    codice_preventivo: null,
    minicart_label: '',
    id_evento: null,
    domini_servizi_aggiuntivi_disponibili: [],
    servizi_aggiuntivi_disponibili: [],
    servizi_extra: { ordini_servizi: [] },
  },
  payment: {
    cro: null,
    method: 'none', // finanziamento, bank, noleggio
    reset: false,
  },
  ui: {
    phase: 0,
    last: 0,
    fetching: false,
    buyHelper: false,
    buyHelperDocumenti: false,
    settings: {},
    sellout_settings_acquista: {},
    sellout_settings_noleggia: {},
  },
  uuid: null,
  secci: {
    percorso_aws: null,
  },
  compass: {
    altre_carte: [],
    politicamente_esposto: 0,
    domicilio_diverso: '0',
    residente_dal: { raw: null },
    data_inizio_attivita: { raw: null },
    data_scadenza_contratto: { raw: null },
    data_apertura_conto: { raw: null },
  },
  compassFileUpload: {
    dropzone: [],
    documenti_necessari: [],
  },
  bonificoFileUpload: {
    dropzone: [],
    documenti_necessari: [],
  },
  findomesticFileUpload: {
    dropzone: [],
    documenti_necessari: [],
  },
  distintaFileUpload: {
    dropzone: [],
  },
  compass_tabelledominio: {
    occupazioneattivita: {},
    settoreattivita: [],
    cittadinanza: [],
    cittadinanza_is_extra_eu_by_codice: {},
  },
  comuni: {
    comune: [],
    nazione: [],
    searchLuogoNascita: [],
    searchCittadinanza: [],
    searchComuneResidenza: [],
    searchLuogoEmissionePatente: [],
    searchLuogoEmissioneCartaIdentita: [],
    searchLuogoEmissionePassaporto: [],
  },
  error: {
    display: false,
    payload: {
      status: null,
      url: null,
      text: null,
    },
  },
  transazioni: {
    conti: {},
  },
  logistica: {
    centri_convezionati: {},
    centro_convenzionato: null,
    codice_provincia: null,
    modal_new_rate_logistica: true,
    centri_convenzionati_by_provincia: {},
  },
  nlt: {
    anticipi: [],
    anticipoNumeroMensilita: {},
    canoni: [],
    canoniIndexed: {},
    canonePersonalizzato: '',
    caricaPrivata: [],
    firma: {},
    dati: {
      altre_fonti_reddito: '',
      altre_fonti_switch: '0',
      anzianita: '',
      anni_apertura_conto: '',
      autocertificazione_1: '',
      autocertificazione_2: '',
      carica_privata: '',
      carica_pubblica: '',
      carica_pubblica_switch: '0',
      consenso_base_privacy_arval: '',
      iban: '',
      nome_societa: '',
      occupazione: '',
      professione: '',
      reddito_coniuge: '',
      reddito_mensile: '',
      settore: '',
      situazione_lavorativa: '',
      situazione_abitativa: '',
      stato_civile: '',
      tipo_contratto: '',
      data_termine_contratto: { raw: null },
      dettaglio_ruolo_pa: '',
      relazione_intestatario_iban: '',
      presa_visione_condizioni_brumbrum: '',
      consenso_base_privacy_brumbrum: '',
    },
    defaultCanone: {},
    documenti_necessari: [],
    dropzone: [],
    minicartLabelCoupon: '',
    numeroMensilita: [],
    occupazione: [],
    selectedAnticipo: null,
    selectedCanone: null,
    selectedNumeroMensilita: null,
    serviziPlus: [],
    serviziPlusByIdTipologia: {},
    serviziPlusSelected: [],
    serviziPlusCosto: 0,
    situazioneLavorativa: [],
    statoCivile: [],
    situazioneAbitativa: [],
    settore: [],
    tipoContratto: [],
    ruoloPa: [],
    stepsVisited: [],
  },
}

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

createReduxEnhancer({
  // Optionally pass options listed below
})

export const store = createStore(
  rootReducer,
  initialState,
  composeEnhancers(
    applyMiddleware(
      loadStoreMiddleware, // sempre per primo!
      thunk,
      fetchMiddleware,
      rerouteMiddleware,
      dumpStoreMiddleware // sempre per ultimo!
    )
  )
)

store.dispatch({
  type: types.SET_UUID,
  payload: sha1(uuid()).substring(0, 7),
})
